<!-- components/courses/ArticleEditor.vue -->
<template>
  <div class="article-editor">
    <div class="editor-container">
      <div class="editor-toolbar">
        <div class="toolbar-left">
          <button class="toolbar-btn" @click="toggleBold" title="Bold">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
              <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
            </svg>
          </button>
          <button class="toolbar-btn" @click="toggleItalic" title="Italic">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <line x1="19" y1="4" x2="10" y2="4" />
              <line x1="14" y1="20" x2="5" y2="20" />
              <line x1="15" y1="4" x2="9" y2="20" />
            </svg>
          </button>
          <button
            class="toolbar-btn"
            @click="toggleUnderline"
            title="Underline"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" />
              <line x1="4" y1="21" x2="20" y2="21" />
            </svg>
          </button>
          <span class="toolbar-divider"></span>
          <button class="toolbar-btn" @click="insertLink" title="Insert Link">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path
                d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
              />
              <path
                d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
              />
            </svg>
          </button>
          <button class="toolbar-btn" @click="insertImage" title="Insert Image">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </button>
          <span class="toolbar-divider"></span>
          <button
            class="toolbar-btn"
            @click="toggleList('bullet')"
            title="Bullet List"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <line x1="8" y1="6" x2="21" y2="6" />
              <line x1="8" y1="12" x2="21" y2="12" />
              <line x1="8" y1="18" x2="21" y2="18" />
              <line x1="3" y1="6" x2="3.01" y2="6" />
              <line x1="3" y1="12" x2="3.01" y2="12" />
              <line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
          </button>
          <button
            class="toolbar-btn"
            @click="toggleList('ordered')"
            title="Numbered List"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <line x1="10" y1="6" x2="21" y2="6" />
              <line x1="10" y1="12" x2="21" y2="12" />
              <line x1="10" y1="18" x2="21" y2="18" />
              <path d="M4 6h1v4" />
              <path d="M4 10h2" />
              <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
            </svg>
          </button>
          <span class="toolbar-divider"></span>
          <button
            class="toolbar-btn"
            @click="toggleBlockquote"
            title="Blockquote"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path
                d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
              />
            </svg>
          </button>
        </div>
        <div class="toolbar-right">
          <button class="toolbar-btn" @click="toggleSource" title="HTML Source">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
          </button>
        </div>
      </div>

      <div v-if="showSource" class="source-editor">
        <textarea v-model="htmlContent" class="source-textarea"></textarea>
      </div>

      <div
        v-else
        class="editor-content"
        ref="editorRef"
        contenteditable="true"
      ></div>
    </div>

    <div class="editor-footer">
      <button class="btn-secondary" @click="emit('cancel')">Cancel</button>
      <button class="btn-primary" @click="saveArticle">Save Article</button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from "vue";

const props = defineProps({
  module: {
    type: Object,
    required: true,
  },
  section: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["saved", "cancel"]);

const editorRef = ref(null);
const htmlContent = ref(props.module.content?.html || "");
const showSource = ref(false);

// Quill-like editor functionality
const getSelection = () => {
  const selection = window.getSelection();
  if (selection.rangeCount > 0) {
    return selection.getRangeAt(0);
  }
  return null;
};

const execCommand = (command, value = null) => {
  document.execCommand(command, false, value);
  editorRef.value?.focus();
};

const toggleBold = () => execCommand("bold");
const toggleItalic = () => execCommand("italic");
const toggleUnderline = () => execCommand("underline");
const toggleBlockquote = () => execCommand("formatBlock", "blockquote");

const toggleList = (type) => {
  if (type === "bullet") {
    execCommand("insertUnorderedList");
  } else {
    execCommand("insertOrderedList");
  }
};

const insertLink = () => {
  const url = prompt("Enter the URL:", "https://");
  if (url) {
    execCommand("createLink", url);
  }
};

const insertImage = () => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.onchange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = `<img src="${e.target.result}" alt="${file.name}" style="max-width: 100%; height: auto;" />`;
        document.execCommand("insertHTML", false, img);
      };
      reader.readAsDataURL(file);
    }
  };
  input.click();
};

const toggleSource = () => {
  if (showSource.value) {
    // Switch back to WYSIWYG
    showSource.value = false;
    setTimeout(() => {
      if (editorRef.value) {
        editorRef.value.innerHTML = htmlContent.value;
      }
    }, 100);
  } else {
    // Switch to source
    if (editorRef.value) {
      htmlContent.value = editorRef.value.innerHTML;
    }
    showSource.value = true;
  }
};

const saveArticle = () => {
  let content = htmlContent.value;
  if (!showSource.value && editorRef.value) {
    content = editorRef.value.innerHTML;
  }
  emit("saved", content);
};

// Initialize editor content
onMounted(() => {
  if (editorRef.value && props.module.content?.html) {
    editorRef.value.innerHTML = props.module.content.html;
  }
});

// Clean up
onBeforeUnmount(() => {
  // Nothing to clean up
});
</script>

<style lang="scss" scoped>
$primary-orange: #ff8000;

.article-editor {
  .editor-container {
    border: 2px solid #e9ecef;
    border-radius: 12px;
    overflow: hidden;

    .editor-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      background: #f8f9fa;
      border-bottom: 1px solid #e9ecef;
      flex-wrap: wrap;
      gap: 4px;

      .toolbar-left,
      .toolbar-right {
        display: flex;
        align-items: center;
        gap: 2px;
        flex-wrap: wrap;
      }

      .toolbar-btn {
        padding: 6px 8px;
        background: none;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        color: #495057;
        transition: all 0.2s ease;

        &:hover {
          background: #e9ecef;
          color: #1a1a1a;
        }

        &:active {
          background: $primary-orange;
          color: #fff;
        }
      }

      .toolbar-divider {
        width: 1px;
        height: 24px;
        background: #dee2e6;
        margin: 0 4px;
      }
    }

    .editor-content {
      padding: 16px;
      min-height: 200px;
      outline: none;
      font-size: 15px;
      line-height: 1.8;

      &:focus {
        outline: 2px solid rgba(255, 128, 0, 0.2);
      }

      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        margin: 16px 0 8px 0;
        color: #1a1a1a;
      }

      p {
        margin: 0 0 8px 0;
        color: #495057;
      }

      ul,
      ol {
        padding-left: 24px;
        margin: 8px 0;
      }

      blockquote {
        border-left: 4px solid $primary-orange;
        padding-left: 16px;
        margin: 8px 0;
        color: #868e96;
        font-style: italic;
      }

      img {
        max-width: 100%;
        height: auto;
        border-radius: 8px;
        margin: 8px 0;
      }

      a {
        color: $primary-orange;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    .source-editor {
      .source-textarea {
        width: 100%;
        min-height: 200px;
        padding: 16px;
        border: none;
        font-family: "Courier New", monospace;
        font-size: 14px;
        outline: none;
        resize: vertical;
        color: #1a1a1a;
        background: #f8f9fa;

        &:focus {
          background: #fff;
        }
      }
    }
  }

  .editor-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 16px;

    .btn-primary {
      padding: 10px 24px;
      background: $primary-orange;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background: darken($primary-orange, 10%);
      }
    }

    .btn-secondary {
      padding: 10px 24px;
      background: #e9ecef;
      border: none;
      border-radius: 8px;
      color: #495057;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background: #dee2e6;
      }
    }
  }
}
</style>
